/* EditorialDialog — the luxury chrome wrapper for every admin pop-up form.
   Wraps Radix `Dialog` (focus trap, escape handler, portal) but strips all
   default chrome — no rounded corners, no shadow, no white card. Instead:
     • Charcoal scrim with backdrop-blur
     • Charcoal panel framed by a single gold-foil hairline
     • Gold breadcrumb · serif italic title · optional italic subtitle
     • Hairline circle close (×) top-right
     • Sticky bottom save bar matching the Site Settings BRANDING pattern:
       pulsing gold dot + UNSAVED CHANGES label + Discard + Save (GoldFoil)
     • Mobile (≤640px): full-screen panel, save bar stays sticky-bottom

   Usage:
     <EditorialDialog
       open={open}
       onClose={() => setOpen(false)}
       breadcrumb="INVOICES · NEW"
       title="New invoice"
       subtitle="Draft an invoice for a client…"
       dirty={isDirty}
       onDiscard={() => reset()}
       onSave={() => submit()}
       saveLabel="SAVE INVOICE →"
       size="md"
     >
       …form fields…
     </EditorialDialog>
*/
import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import GoldFoilButton from "@/components/editorial/GoldFoilButton";
import HairlineButton from "@/components/editorial/HairlineButton";

const SIZE = {
  sm: "sm:max-w-[480px]",
  md: "sm:max-w-[640px]",
  lg: "sm:max-w-[900px]",
};

export default function EditorialDialog({
  open,
  onClose,
  breadcrumb,
  title,
  subtitle,
  dirty = false,
  onDiscard,
  onSave,
  saveLabel = "SAVE",
  size = "md",
  className = "",
  children,
  ...rest
}) {
  const showSaveBar = typeof onSave === "function";
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className={`fixed inset-0 sm:inset-auto sm:left-[50%] sm:top-[50%] z-50 w-full ${SIZE[size] || SIZE.md} sm:translate-x-[-50%] sm:translate-y-[-50%] bg-[#0E0D0C] text-[color:var(--cream)] border border-[color:var(--gold-foil)]/40 rounded-none flex flex-col max-h-screen sm:max-h-[90vh] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${className}`}
          aria-describedby={undefined}
          {...rest}
        >
          {/* Header */}
          <div className="relative px-6 sm:px-8 pt-7 pb-5 border-b border-[color:var(--gold-foil)]/20">
            {breadcrumb && (
              <p className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-3">
                {breadcrumb}
              </p>
            )}
            <DialogPrimitive.Title asChild>
              <h2
                className="font-serif italic text-3xl sm:text-4xl font-light text-[color:var(--cream)] leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {title}
              </h2>
            </DialogPrimitive.Title>
            {subtitle && (
              <p
                className="mt-3 font-serif italic text-sm sm:text-base text-[color:var(--cream)]/65 max-w-xl leading-relaxed"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {subtitle}
              </p>
            )}
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close"
                data-testid="editorial-dialog-close"
                className="absolute right-5 top-5 w-9 h-9 inline-flex items-center justify-center border border-[color:var(--gold-foil)]/40 rounded-full text-[color:var(--cream)]/80 hover:text-[color:var(--cream)] hover:border-[color:var(--gold-base)] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </DialogPrimitive.Close>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-8 py-7">
            {children}
          </div>

          {/* Bottom save bar (flex sibling pinned to the panel's bottom edge) */}
          {showSaveBar && (
            <div className="shrink-0 px-6 sm:px-8 py-4 border-t border-[color:var(--gold-foil)]/25 bg-[#0E0D0C] flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {dirty ? (
                  <>
                    <span
                      className="w-2 h-2 rounded-full bg-[color:var(--gold-base)] shrink-0 animate-pulse"
                      aria-hidden
                    />
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] truncate">
                      Unsaved changes
                    </span>
                  </>
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--cream)]/40 truncate">
                    No pending changes
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {dirty && onDiscard && (
                  <HairlineButton
                    onClick={onDiscard}
                    tone="dark"
                    arrow={false}
                    size="sm"
                    data-testid="editorial-dialog-discard"
                  >
                    Discard
                  </HairlineButton>
                )}
                <GoldFoilButton
                  onClick={onSave}
                  tone="dark"
                  size="sm"
                  arrow={false}
                  data-testid="editorial-dialog-save"
                  disabled={!dirty}
                  className={!dirty ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {saveLabel}
                </GoldFoilButton>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* EditorialField — labelled hairline-underline field. Pairs the gold uppercase
   label with the global `.input` styling. Use this for any text/number/date/
   email field inside an EditorialDialog. */
export function EditorialField({
  label,
  required,
  hint,
  className = "",
  children,
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-2">
        {label}
        {required && <span className="text-[color:var(--gold-base)]/70 ml-1">*</span>}
      </span>
      {children}
      {hint && (
        <span className="mt-2 block text-[11px] text-[color:var(--cream)]/45 italic font-serif" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {hint}
        </span>
      )}
    </label>
  );
}
